import React from 'react';
import useIsReadOnlyMode from '../../hooks/useIsReadOnlyMode';
import {
  useCreateEpdProductOtherDangerMaterial,
  useDeleteProductOtherDangerMaterials,
  useUpdateEpdProductOtherDangerMaterial,
} from 'services/api/mutations';
import { useEpdProductOtherDangerMaterials } from 'services/api/queries';
import { EPDContentDeclarationProductType } from 'types/content-declaration/EPDContentDeclarationProductType';
import useGridRowSelectionViaCheckbox from '../useGridRowSelectionViaCheckbox';
import { OtherDangerMaterialModel } from 'types/content-declaration/OtherDangerMaterialModel';
import useDynamicGridWithEmptyFirstRow from '../useDynamicGridWithEmptyFirstRow';
import DynamicGridHeader from '../DynamicGridHeader';
import { DataTableStyled } from '../StyledGrid';
import { Column } from 'primereact/column';
import { cellNumberEditor, cellTextEditor } from '../GridCellEditors';
import { numericCellBody, textCellBody } from '../GridService';
import { useContentDeclarationTerminologyHelper } from '../../hooks/useContentDeclarationTerminologyHelper';

interface Props {
  epdVersionId: string;
  contentDeclarationType: EPDContentDeclarationProductType;
}

const OtherDangerMaterialsGrid: React.FC<Props> = ({ epdVersionId, contentDeclarationType }) => {
  const isReadOnly = useIsReadOnlyMode();
  const { replaceInString, placeholders } = useContentDeclarationTerminologyHelper();

  const rows = useEpdProductOtherDangerMaterials(epdVersionId, contentDeclarationType).data;
  const createMutation = useCreateEpdProductOtherDangerMaterial(epdVersionId, contentDeclarationType);
  const updateMutation = useUpdateEpdProductOtherDangerMaterial(epdVersionId, contentDeclarationType);
  const deleteMutation = useDeleteProductOtherDangerMaterials(epdVersionId, contentDeclarationType);

  const { dataTableProps, checkboxColumnNode, handleDeleteSelected, isDeleteDisabled, gridId } =
    useGridRowSelectionViaCheckbox<OtherDangerMaterialModel>(isReadOnly, (deletedRows) =>
      deleteMutation.mutate(deletedRows.map((x) => x.ord))
    );

  const rowClassName = () => ({ 'p-disabled': isReadOnly });

  const { gridData, handleAddRow, handleCellEdit } = useDynamicGridWithEmptyFirstRow<OtherDangerMaterialModel>({
    rows,
    createMutation,
    updateMutation,
    emptyEntity: {
      name: '',
      ecNo: '',
      casNo: '',
      weightPercent: null,
    },
  });

  const header = (
    <DynamicGridHeader
      caption="Other hazardous and toxic substances if required by normative standards or regulation"
      onAddRow={handleAddRow}
      onDeleteRows={handleDeleteSelected}
      deleteRowsDisabled={isDeleteDisabled}
    />
  );

  return (
    <DataTableStyled
      key={gridId}
      header={header}
      value={gridData}
      dataKey="ord"
      tableStyle={{ minWidth: '49rem', tableLayout: 'fixed' }}
      showGridlines
      editMode="cell"
      rowClassName={rowClassName}
      {...dataTableProps}
    >
      {checkboxColumnNode}

      <Column
        style={{ wordBreak: 'break-all' }}
        field="name"
        header="Hazardous/Toxic substances"
        editor={isReadOnly ? undefined : (options) => cellTextEditor(options, 'Type here', 512)}
        body={textCellBody({ placeholder: 'Type here' })}
        onCellEditComplete={handleCellEdit}
      />

      <Column
        field="ecNo"
        header="EC No."
        editor={isReadOnly ? undefined : (options) => cellTextEditor(options, 'Type here', 31)}
        body={textCellBody({ placeholder: 'Type here' })}
        onCellEditComplete={handleCellEdit}
        style={{ width: '7rem' }}
      />

      <Column
        field="casNo"
        header="CAS No."
        editor={isReadOnly ? undefined : (options) => cellTextEditor(options, 'Type here', 63)}
        body={textCellBody({ placeholder: 'Type here' })}
        onCellEditComplete={handleCellEdit}
        style={{ width: '7rem' }}
      />

      <Column
        field="weightPercent"
        header={replaceInString('Weight per functional or declared unit %')}
        editor={isReadOnly ? undefined : cellNumberEditor}
        onCellEditComplete={handleCellEdit}
        body={numericCellBody({ placeholder: placeholders.typeWeight })}
        style={{ width: '15rem' }}
      />
    </DataTableStyled>
  );
};

export default OtherDangerMaterialsGrid;
